import {
  Box,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Grid,
  IconButton,
  Stack,
  SvgIcon,
  Typography,
} from '@mui/material';
import { ReactElement, useEffect, useState } from 'react';
import {
  AppPrimaryButton,
  ConfirmDialog,
} from 'shared/components/AppComponents';
import PageHeader from 'shared/components/PageHeader';
import { ReactComponent as Document } from 'assets/images/ic_document.svg';
import DownloadIcon from '@mui/icons-material/Download';
import DeleteIcon from '@mui/icons-material/Delete';
import { ReactComponent as Upload } from 'assets/images/ic_upload.svg';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import UploadModal from './UploadModal';
import { TEVVService } from 'services/TEVVService';
import { Utils } from 'shared/helpers';
import moment from 'moment';
import { downloadFileSaveAs } from 'shared/helpers/DirectDownload';
import { DocumentProps, TemplateProps } from '../Main';
import { BaseApplicationState } from 'state/baseapplication/Reducer';
import { useSelector } from 'react-redux';

const Documents = (props: any): ReactElement => {
  const {
    documentList,
    getDocumentList,
    templateList,
    downloadFile,
  }: {
    templateList: TemplateProps[];
    documentList: DocumentProps[];
    getDocumentList: () => void;
    downloadFile: (templateId: string, fileId: string) => void;
  } = props;
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showUpload, setShowUpload] = useState(false);
  const [selectedTemplate, setSelectedTempalte] =
    useState<DocumentProps | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const { orgACLConfig }: BaseApplicationState = useSelector(
    (state: any) => state.baseAppState
  );
  const handleCloseCloseDocument = (): void => {
    setShowUpload(false);
  };

  const actionOnDownload = (file: DocumentProps): void => {
    const { templateId, fileDetails } = file;
    downloadFileSaveAs(fileDetails.fileUrl, fileDetails.originalFilename);
    downloadFile(templateId, fileDetails.fileId);
  };

  const handleCloseDelete = (): void => {
    setShowDeleteModal(false);
  };
  const handleDeleteAISystem = (): void => {
    if (!selectedTemplate) return;
    const { templateId, fileDetails } = selectedTemplate;
    const { _id } = fileDetails;
    TEVVService.deleteFiles(templateId, _id)
      .then((res) => {
        getDocumentList();
        Utils.handleSuccessResponse(res);
      })
      .catch((error) => {
        Utils.handleErrorResponse(error);
      });

    setShowDeleteModal(false);
  };

  useEffect(() => {
    setIsLoading(true);

    const timeoutId = setTimeout(() => {
      setIsLoading(false);
    }, 500);

    return () => clearTimeout(timeoutId);
  }, []);

  return (
    <>
      {isLoading && (
        <Box className="empty-content">
          <CircularProgress />
        </Box>
      )}
      {!isLoading && (
        <>
          <PageHeader
            title="TEVV Documents"
            description={`${documentList.length} Documents available`}
            actions={
              <Stack spacing={2} direction="row">
                {orgACLConfig.aiInventory?.includes(
                  'aiInventory.aiSystem.aiInventory.view.tevv.documents.uploadDocument'
                ) && (
                  <AppPrimaryButton
                    className="btn-small "
                    title="Upload Document"
                    onClick={() => {
                      setShowUpload(true);
                    }}
                    startIcon={
                      <SvgIcon
                        component={Upload}
                        inheritViewBox
                        className="view-icon"
                        fontSize="inherit"
                      />
                    }
                  />
                )}
              </Stack>
            }
          />
          {documentList.length === 0 && (
            <Box className="feedback-container">
              <Card variant="outlined">
                <CardContent>
                  <SvgIcon
                    component={Document}
                    inheritViewBox
                    className="view-icon"
                    fontSize="inherit"
                  />
                  <Stack
                    direction="column"
                    spacing={4}
                    justifyContent="center"
                    alignItems="center"
                  >
                    <Typography variant="h6">No TEVV documents yet</Typography>
                    <Typography component="span">
                      Get started by uploading your first Test, Evaluation,
                      Verification & Validation document.
                    </Typography>
                    {orgACLConfig.aiInventory?.includes(
                      'aiInventory.aiSystem.aiInventory.view.tevv.documents.uploadFirstDocument'
                    ) && (
                      <AppPrimaryButton
                        title="Upload Your First Document"
                        onClick={() => {
                          setShowUpload(true);
                        }}
                        startIcon={
                          <SvgIcon
                            component={Upload}
                            inheritViewBox
                            className="view-icon"
                            fontSize="inherit"
                            htmlColor="#ffff"
                          />
                        }
                        // onClick={() => navigate('/incidents/add')}
                      />
                    )}
                  </Stack>
                </CardContent>
              </Card>
            </Box>
          )}
          {documentList.length > 0 && (
            <Grid container mt={4} spacing={3}>
              {documentList.map((document: DocumentProps) => (
                <Grid item xs={4} spacing={4}>
                  <Card
                    className="p-4"
                    sx={{ flex: 1, height: '100%', boxSizing: 'border-box' }}
                  >
                    <Box className="justify-space">
                      <Box className="align-center">
                        <SvgIcon
                          component={Document}
                          inheritViewBox
                          className="view-icon"
                          fontSize="small"
                          sx={{ color: 'text.primary' }}
                        />

                        <Typography className="mx-2 f16 fw-5 word-break-all">
                          {document.fileDetails.originalFilename}
                        </Typography>
                      </Box>
                      <Box className="align-center">
                        {orgACLConfig.aiInventory?.includes(
                          'aiInventory.aiSystem.aiInventory.view.tevv.documents.dowloadDocument'
                        ) && (
                          <IconButton
                            onClick={() => {
                              actionOnDownload(document);
                            }}
                          >
                            <DownloadIcon
                              fontSize="small"
                              sx={{ color: 'text.primary' }}
                            />
                          </IconButton>
                        )}
                        {orgACLConfig.aiInventory?.includes(
                          'aiInventory.aiSystem.aiInventory.view.tevv.documents.deleteDocument'
                        ) && (
                          <IconButton
                            onClick={() => {
                              setSelectedTempalte(document);
                              setShowDeleteModal(true);
                            }}
                          >
                            <DeleteIcon
                              fontSize="small"
                              sx={{ color: 'text.primary' }}
                            />
                          </IconButton>
                        )}
                      </Box>
                    </Box>
                    <Stack
                      direction="row"
                      className="my-2"
                      sx={{ flexWrap: 'wrap', rowGap: '10px' }}
                    >
                      <Chip
                        label={document.stage}
                        sx={{
                          background: 'text.secondary',
                          color: 'text.primary',
                          border: '1px solid black',
                          marginRight: '5px',
                        }}
                        size="small"
                      />
                      <Chip
                        label={document.category}
                        sx={{
                          background: 'white',
                          color: 'text.primary',
                          border: '1px solid black',
                        }}
                        size="small"
                      />
                    </Stack>
                    <Box className="justify-space mt-3">
                      <Box className="align-center">
                        <CalendarMonthIcon fontSize="small" />
                        <Typography variant="body2">
                          {moment(document.fileDetails.createdAt).format(
                            'MMM DD YYYY'
                          )}
                        </Typography>
                      </Box>
                      {/* <Box className="flex">
                      <Typography variant="body2" className="mx-2">
                        v1.20
                      </Typography>
                      <Typography variant="body2">0.50mb</Typography>
                    </Box> */}
                    </Box>
                  </Card>
                </Grid>
              ))}
            </Grid>
          )}
        </>
      )}
      <ConfirmDialog
        title="Delete TEVV Document"
        subTitle="Are you sure to delete the document?"
        showConfirm={showDeleteModal}
        cancelTitle="Cancel"
        successTitle="Delete"
        onSuccess={() => {
          handleDeleteAISystem();
        }}
        onCancel={handleCloseDelete}
        onCloseDialog={handleCloseDelete}
      />
      <UploadModal
        title="Upload TEVV Document"
        subtitle="TEVV"
        openDocumentModal={showUpload}
        cancelTitle="Cancel"
        successTitle="Upload"
        templateList={templateList}
        onSuccess={() => {
          handleCloseCloseDocument();
          getDocumentList();
          // handleDeleteAISystem(aiSystemId);
        }}
        getDocumentList={getDocumentList}
        onCancel={handleCloseCloseDocument}
      />
    </>
  );
};

export default Documents;
